import React, { FC, useState, useCallback, useMemo } from 'react'
import { TradeConditionFlag, UserPreference, TradingEntity, CounterpartyInstruction } from 'api'
import styled from '@emotion/styled'
import * as mixins from 'view/styles/mixins'
import * as constants from 'view/styles/constants'
import { Translation, Locale } from 'locale'
import { debounce } from 'lodash-es'
import { PositionsGrouping, getIsByAssets } from 'view/routes/staging/SendRFQPanel/rfqPanelUtils'
import { CollapsePanel } from 'view/components/panels'
import { PreparedStagingOrder } from 'store/selection/selectionTypes'
import { PositionsGroupingSelectbox } from './PositionsGroupingSelectbox'
import { arrowUpDownIcon } from 'view/components/datagrid/styles/gridIconSet'
import { PositionList } from './PositionList'
import { MultiButton } from 'view/components/buttons'

type CollapsedItems = Map<string, boolean>

const ContentWrapper = styled.div`
  width: 100%;
  height: auto;
  padding: 20px 12px;
`

const CollapseAllButton = styled(MultiButton)`
  background-color: ${constants.CORE_PRIMARY};
  border-radius: 25px;
  width: 19px;
  height: 19px;
  & :active,
  & :hover {
    background-color: ${constants.CORE_PRIMARY_3};
  }
  margin-right: 8px;
`

const SectionHeader = styled.div`
  button {
    outline: none;
  }
  ${mixins.flexCenterContentCrossAxis};
`

export interface PositionsSectionProps {
  counterpartyInstructions: CounterpartyInstruction[]
  readOnly: boolean
  ordersToPrepare: PreparedStagingOrder[]
  userPreference: UserPreference
  getTradingEntity: (entityId: string) => TradingEntity | null
  onChangePosition: (orderIds: string[], counterpartiesId: string[], isAxed: boolean, tradeConditionFlag: TradeConditionFlag) => void
}

export const PositionsSection: FC<PositionsSectionProps> = props => {
  const { counterpartyInstructions, ordersToPrepare } = props
  const [collapsedItems, setCollapsedItems] = useState<CollapsedItems>(new Map())
  const [chosenGrouping, setChosenGrouping] = useState<PositionsGrouping>(Translation.SC_GROUP_BY_ASSETS)
  const isByAssets = useMemo(() => getIsByAssets(chosenGrouping), [chosenGrouping])
  const ordersIds = useMemo(() => ordersToPrepare.map(order => order.orderDetail.order.orderId), [ordersToPrepare])
  const ids = useMemo(() => (isByAssets ? ordersIds : counterpartyInstructions.map(ci => ci.counterpartyId)), [
    isByAssets,
    ordersIds,
    counterpartyInstructions
  ])

  const getSectionSummary = useCallback(() => {
    let rolls = 0
    let unwinds = 0
    let axes = 0
    let subs = 0

    ordersToPrepare.forEach(orderToPrepare => {
      orderToPrepare.negotiationInstruction.counterpartyInstructions.forEach(counterpartyInstruction => {
        if (counterpartyInstruction.isAxed) {
          axes++
        }
        if (counterpartyInstruction.tradeConditionFlag === TradeConditionFlag.ROLL) {
          rolls++
        }
        if (counterpartyInstruction.tradeConditionFlag === TradeConditionFlag.UNWIND) {
          unwinds++
        }
        if (counterpartyInstruction.tradeConditionFlag === TradeConditionFlag.SUB) {
          subs++
        }
      })
    })
    return `${Locale.translate(Translation.SC_POSITION_SUMMARY, { axes, rolls, unwinds, subs })}`
  }, [ordersToPrepare])

  const getIsItemCollapsed = useCallback(
    (itemId: string) => {
      return collapsedItems.get(itemId) === true
    },
    [collapsedItems]
  )

  const handlePositionGroupingUpdate = useCallback(
    (value: PositionsGrouping) => {
      setCollapsedItems(new Map())
      setChosenGrouping(value)
    },
    [setCollapsedItems, setChosenGrouping]
  )

  const toggleItemCollapse = useCallback(
    (itemId: string) => {
      const newCollapsedItems = new Map(collapsedItems)
      const isCollapsed = newCollapsedItems.get(itemId)
      newCollapsedItems.set(itemId, !isCollapsed)
      setCollapsedItems(newCollapsedItems)
    },
    [collapsedItems, setCollapsedItems]
  )

  const toggleAllItemsCollapse = useCallback(() => {
    const someOrdersAreCollapsed = [...collapsedItems.values()].some(value => value === true)
    const newCollapsedItems = new Map(collapsedItems)

    ids.forEach(id => {
      if (someOrdersAreCollapsed) {
        newCollapsedItems.set(id, false)
      } else {
        newCollapsedItems.set(id, true)
      }
    })

    setCollapsedItems(newCollapsedItems)
  }, [collapsedItems, setCollapsedItems, ids])

  return counterpartyInstructions.length > 0 ? (
    <CollapsePanel
      dataTestId={'SendRFQPanel-Positions'}
      title={Locale.translate(Translation.TC_POSITIONS)}
      subtitle={getSectionSummary()}
      defaultCollapsed={false}>
      <ContentWrapper>
        <SectionHeader>
          <CollapseAllButton onClick={debounce(toggleAllItemsCollapse, 250)}>{arrowUpDownIcon}</CollapseAllButton>
          <PositionsGroupingSelectbox value={chosenGrouping} onUpdate={handlePositionGroupingUpdate} />
        </SectionHeader>
        <PositionList
          {...props}
          getIsItemCollapsed={getIsItemCollapsed}
          toggleItemCollapse={toggleItemCollapse}
          chosenGrouping={chosenGrouping}
        />
      </ContentWrapper>
    </CollapsePanel>
  ) : null
}
